import type AliRtcEngine from 'aliyun-rtc-sdk';
import type { RemoteVideoBinding } from './live-call';
import { normalizeError, pickDisplayBinding } from './live-call';
import { teardownRtcEngine, waitForRtcEngineTeardown } from './rtc-engine-teardown';

type RtcSdkModule = typeof import('aliyun-rtc-sdk');

export type LocalVideoSource = 'camera' | 'none' | 'screen';

// 屏幕采集帧通过 camera 槽位（streamType=camera）上行：后端 unidu 只对 camera track
// 的裸帧做 OnRemoteVideoSample 回调，屏幕辅流（screen track）的裸帧 native core service
// 默认不回调，故不能走 startScreenShare。把 getDisplayMedia 拿到的 track 直接塞进 camera
// 槽位发布即可复用已验证的 camera 裸帧链路。createLocalStream 优先，switchCamera 兜底。
type RtcCameraInjectEngine = AliRtcEngine & {
  publisher?: {
    createLocalStream?: (options: { audio?: boolean; mediaStream?: MediaStream; video?: boolean }) => Promise<unknown>;
  };
  switchCamera?: (deviceId?: string, videoTrack?: MediaStreamTrack) => Promise<void>;
};

const injectScreenTrackAsCamera = async (
  engine: AliRtcEngine,
  stream: MediaStream,
  track: MediaStreamTrack,
): Promise<void> => {
  const candidate = engine as RtcCameraInjectEngine;
  const createLocalStream = candidate.publisher?.createLocalStream?.bind(candidate.publisher);
  if (createLocalStream) {
    await createLocalStream({ audio: false, mediaStream: stream, video: true });
    return;
  }
  if (typeof candidate.switchCamera === 'function') {
    await candidate.switchCamera(undefined, track);
    return;
  }
  throw new Error('screen share camera-inject methods unavailable');
};

const LIVE_SCREEN_SHARE_LOG_PREFIX = '[live-screen-share]';

const warnScreenShare = (stage: string, data?: Record<string, unknown>): void => {
  console.warn(LIVE_SCREEN_SHARE_LOG_PREFIX, stage, data ?? {});
};

const errorScreenShare = (stage: string, err: unknown, data?: Record<string, unknown>): void => {
  const details = {
    error_name: err instanceof Error ? err.name : typeof err,
    error_code: err instanceof Error ? (err as Error & { code?: unknown }).code : undefined,
    error_message: err instanceof Error ? err.message : String(err),
    ...(data ?? {}),
  };
  console.error(LIVE_SCREEN_SHARE_LOG_PREFIX, stage, details, err);
};

const isBrowserScreenShareSupported = (): boolean => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }
  if (!window.isSecureContext) {
    return false;
  }
  return typeof navigator.mediaDevices?.getDisplayMedia === 'function';
};

const isUserCancelledScreenShareError = (err: unknown): boolean => {
  if (!(err instanceof Error)) {
    return false;
  }
  return err.name === 'NotAllowedError' || err.name === 'AbortError';
};

interface RtcMediaTrackPresence {
  hasAudioTrack: boolean;
  hasVideoTrack: boolean;
  uid: string;
}

interface RtcMediaStatsSnapshot {
  audioBytesReceived?: number;
  audioPacketsReceived?: number;
  hasAudioTrack?: boolean;
  hasVideoTrack?: boolean;
  sampledAtMs: number;
  subscribeLoss?: number;
  subscribeQuality?: number;
  subscribeRttMs?: number;
  uid: string | null;
  videoBytesReceived?: number;
  videoDecodeFps?: number;
  videoFramesDecoded?: number;
  videoFramesReceived?: number;
  videoFramesRendered?: number;
  videoFrameHeight?: number;
  videoFrameWidth?: number;
  videoPacketsReceived?: number;
}

const LOCAL_CAMERA_FRAME_RATE = 5;
const LOCAL_AUDIO_RESTORE_RETRY_DELAYS_MS = [0, 1_000, 3_000] as const;

// 屏幕采集上行分辨率上限 480p（16:9 = 854×480）。后端 OnRemoteVideoSample 拿到的是解码后裸帧，
// 体积主要由分辨率决定（RTC 网络压缩到不了业务侧）。
// createLocalStream 注入 camera 槽位时，SDK 会按默认 camera profile 把 track 改回约 1280×720，
// 因此除了采集后 clamp，还必须在 publish 后再 clamp 一次。
const SCREEN_SHARE_MAX_WIDTH = 854;
const SCREEN_SHARE_MAX_HEIGHT = 480;
const SCREEN_SHARE_VIDEO_CONSTRAINTS = {
  width: { max: SCREEN_SHARE_MAX_WIDTH },
  height: { max: SCREEN_SHARE_MAX_HEIGHT },
} as const;

const clampScreenShareTrackConstraints = async (track: MediaStreamTrack): Promise<void> => {
  if (typeof track.applyConstraints !== 'function') {
    return;
  }

  try {
    await track.applyConstraints(SCREEN_SHARE_VIDEO_CONSTRAINTS);
  } catch {
    // 约束失败不阻断共享；部分浏览器/SDK 路径可能忽略或稍后覆盖分辨率。
  }
};

const isRtcConnectedStatus = (rtcSdk: RtcSdkModule, status: number | string): boolean => {
  return status === rtcSdk.AliRtcConnectionStatus.AliRtcConnectionStatusConnected;
};

const isRtcFailureStatus = (rtcSdk: RtcSdkModule, status: number | string): boolean => {
  return status === rtcSdk.AliRtcConnectionStatus.AliRtcConnectionStatusFailed;
};

const isRtcReconnectingStatus = (rtcSdk: RtcSdkModule, status: number | string): boolean => {
  return status === rtcSdk.AliRtcConnectionStatus.AliRtcConnectionStatusReconnecting;
};

interface LiveRtcSessionParams {
  loadRtcSdk?: () => Promise<RtcSdkModule>;
  onAutoPlayBlocked: () => void;
  onCameraError: (message: string | null) => void;
  onConnectionEstablished: () => void;
  onConnectionFailure: () => void;
  onConnectionRecovering?: () => void;
  onLocalAudioEnabledChange: (enabled: boolean) => void;
  onLocalAudioRestoreError?: (message: string | null) => void;
  onLocalCameraEnabledChange?: (enabled: boolean) => void;
  onLocalVideoSourceChange?: (source: LocalVideoSource) => void;
  onRemoteMediaStatsChange: (stats: RtcMediaStatsSnapshot) => void;
  onRemoteTrackPresenceChange: (presence: RtcMediaTrackPresence) => void;
  onRemoteBindingsChange: (bindings: RemoteVideoBinding[]) => void;
  onScreenShareError?: (message: string | null) => void;
  onScreenShareSupportedChange?: (supported: boolean) => void;
}

interface StartOptions {
  rtcAuthInfo: ReturnType<typeof import('./live-call').parseRtcAuthInfo>;
  rtcUserId: string;
  token: string;
}

export class LiveRtcSession {
  private readonly loadRtcSdk: () => Promise<RtcSdkModule>;
  private readonly onAutoPlayBlocked: LiveRtcSessionParams['onAutoPlayBlocked'];
  private readonly onCameraError: LiveRtcSessionParams['onCameraError'];
  private readonly onConnectionEstablished: LiveRtcSessionParams['onConnectionEstablished'];
  private readonly onConnectionFailure: LiveRtcSessionParams['onConnectionFailure'];
  private readonly onConnectionRecovering: NonNullable<LiveRtcSessionParams['onConnectionRecovering']>;
  private readonly onLocalAudioEnabledChange: LiveRtcSessionParams['onLocalAudioEnabledChange'];
  private readonly onLocalAudioRestoreError: NonNullable<LiveRtcSessionParams['onLocalAudioRestoreError']>;
  private readonly onLocalCameraEnabledChange: NonNullable<LiveRtcSessionParams['onLocalCameraEnabledChange']>;
  private readonly onLocalVideoSourceChange: NonNullable<LiveRtcSessionParams['onLocalVideoSourceChange']>;
  private readonly onScreenShareError: NonNullable<LiveRtcSessionParams['onScreenShareError']>;
  private readonly onScreenShareSupportedChange: NonNullable<LiveRtcSessionParams['onScreenShareSupportedChange']>;
  private readonly onRemoteMediaStatsChange: LiveRtcSessionParams['onRemoteMediaStatsChange'];
  private readonly onRemoteTrackPresenceChange: LiveRtcSessionParams['onRemoteTrackPresenceChange'];
  private readonly onRemoteBindingsChange: LiveRtcSessionParams['onRemoteBindingsChange'];

  private autoPlayBlockedAudioUsers = new Set<string>();
  private engine: AliRtcEngine | null = null;
  private disposed = false;
  private desiredLocalAudioEnabled = true;
  private desiredLocalCameraEnabled = false;
  private localVideoElement: HTMLVideoElement | null = null;
  private localVideoSource: LocalVideoSource = 'none';
  private remoteBindings: RemoteVideoBinding[] = [];
  private remoteVideoElement: HTMLVideoElement | null = null;
  private rtcSdk: RtcSdkModule | null = null;
  private screenShareStream: MediaStream | null = null;
  private screenShareTrack: MediaStreamTrack | null = null;
  private screenShareTrackEndedListener: (() => void) | null = null;

  constructor({
    loadRtcSdk = () => import('aliyun-rtc-sdk'),
    onAutoPlayBlocked,
    onCameraError,
    onConnectionEstablished,
    onConnectionFailure,
    onConnectionRecovering = () => {},
    onLocalAudioEnabledChange,
    onLocalAudioRestoreError = () => {},
    onLocalCameraEnabledChange = () => {},
    onLocalVideoSourceChange = () => {},
    onRemoteMediaStatsChange,
    onRemoteTrackPresenceChange,
    onRemoteBindingsChange,
    onScreenShareError = () => {},
    onScreenShareSupportedChange = () => {},
  }: LiveRtcSessionParams) {
    this.loadRtcSdk = loadRtcSdk;
    this.onAutoPlayBlocked = onAutoPlayBlocked;
    this.onCameraError = onCameraError;
    this.onConnectionEstablished = onConnectionEstablished;
    this.onConnectionFailure = onConnectionFailure;
    this.onConnectionRecovering = onConnectionRecovering;
    this.onLocalAudioEnabledChange = onLocalAudioEnabledChange;
    this.onLocalAudioRestoreError = onLocalAudioRestoreError;
    this.onLocalCameraEnabledChange = onLocalCameraEnabledChange;
    this.onLocalVideoSourceChange = onLocalVideoSourceChange;
    this.onRemoteMediaStatsChange = onRemoteMediaStatsChange;
    this.onRemoteTrackPresenceChange = onRemoteTrackPresenceChange;
    this.onRemoteBindingsChange = onRemoteBindingsChange;
    this.onScreenShareError = onScreenShareError;
    this.onScreenShareSupportedChange = onScreenShareSupportedChange;
  }

  bindRemoteView(element: HTMLVideoElement | null): void {
    this.remoteVideoElement = element;
    this.applyRemoteView();
  }

  bindLocalView(element: HTMLVideoElement | null): void {
    this.localVideoElement = element;
  }

  async start({ rtcAuthInfo, rtcUserId, token }: StartOptions): Promise<void> {
    if (this.disposed || this.engine) {
      return;
    }

    await waitForRtcEngineTeardown();
    const rtcSdk = await this.getRtcSdk();
    await this.probeScreenShareSupport(rtcSdk);
    // enableBlankAudio: 开启后 SDK 内部 BlankAudioPlugin 会在 mute 期间持续往上行推空白音频，
    // 保持音频轨道活跃，避免对端 AI 因 6s 收不到 audio packet 触发 INBOUND_TIMEOUT
    // 判定用户掉线。等价于 iOS 端的 whiteNoiseAudioPusher，但由 SDK 自身托管，
    // 不需要我们手动 switchMicrophone 换 noise track。
    const engine = rtcSdk.default.getInstance({
      parameter: { enableBlankAudio: true },
    });
    this.engine = engine;

    try {
      engine.setChannelProfile(rtcSdk.AliRtcSdkChannelProfile.AliRtcSdkInteractiveLive);
      await engine.setClientRole(rtcSdk.AliRtcSdkClientRole.AliRtcSdkInteractive);
      await engine.setAudioProfile('standard');
      engine.setPlayoutVolume(100);
      engine.setRecordingVolume(100);
      engine.muteAllRemoteAudioPlaying(false);
      await engine.startAudioCapture({
        autoGainControl: true,
        echoCancellation: true,
        noiseSuppression: true,
      });
      await engine.publishLocalAudioStream(true);
      engine.muteLocalMic(!this.desiredLocalAudioEnabled);
      this.onLocalAudioEnabledChange(this.desiredLocalAudioEnabled);
      engine.publishLocalVideoStream(false);
      this.onLocalCameraEnabledChange(this.desiredLocalCameraEnabled);
      engine.setDefaultSubscribeAllRemoteAudioStreams(true);
      engine.subscribeAllRemoteAudioStreams(true);
      engine.setDefaultSubscribeAllRemoteVideoStreams(true);
      engine.subscribeAllRemoteVideoStreams(true);

      engine.on('connectionStatusChange', (status) => {
        if (this.disposed || this.engine !== engine) {
          return;
        }

        if (isRtcConnectedStatus(rtcSdk, status)) {
          this.onConnectionEstablished();
          return;
        }

        if (isRtcReconnectingStatus(rtcSdk, status)) {
          this.onConnectionRecovering();
          return;
        }

        if (isRtcFailureStatus(rtcSdk, status)) {
          this.onConnectionFailure();
        }
      });

      engine.on('remoteUserOffLineNotify', (uid: string) => {
        if (this.disposed || this.engine !== engine) {
          return;
        }

        this.autoPlayBlockedAudioUsers.delete(uid);
        this.removeRemoteBinding(uid);
      });

      engine.on('remoteTrackAvailableNotify', (uid, audioTrack, videoTrack) => {
        if (this.disposed || this.engine !== engine) {
          return;
        }

        this.onRemoteTrackPresenceChange({
          hasAudioTrack: audioTrack !== rtcSdk.AliRtcAudioTrack.AliRtcAudioTrackNo,
          hasVideoTrack: videoTrack !== rtcSdk.AliRtcVideoTrack.AliRtcVideoTrackNo,
          uid,
        });

        if (videoTrack === rtcSdk.AliRtcVideoTrack.AliRtcVideoTrackNo) {
          this.removeRemoteBinding(uid);
        } else {
          engine.subscribeRemoteMediaStream(
            uid,
            videoTrack,
            true,
            audioTrack !== rtcSdk.AliRtcAudioTrack.AliRtcAudioTrackNo,
          );
          engine.resumeRemoteMediaStream(uid, videoTrack);
          this.upsertRemoteBinding({ uid, audioTrack, videoTrack });
        }

        if (audioTrack !== rtcSdk.AliRtcAudioTrack.AliRtcAudioTrackNo) {
          engine.subscribeRemoteAudioStream(uid, true);
          engine.muteRemoteAudioPlaying(uid, false);
          engine.setRemoteAudioVolume(uid, 100);
          engine.resumeRemoteAudioStream(uid);
        }
      });

      engine.on('remoteAudioAutoPlayFail', (uid: string) => {
        if (this.disposed || this.engine !== engine) {
          return;
        }

        this.autoPlayBlockedAudioUsers.add(uid);
        this.onAutoPlayBlocked();
      });

      engine.on('remoteVideoAutoPlayFail', () => {
        if (this.disposed || this.engine !== engine) {
          return;
        }

        this.onAutoPlayBlocked();
      });

      engine.on('rtcRemoteVideoStats', (stats: Array<{ stats?: Record<string, unknown>; uid?: string }>) => {
        if (this.disposed || this.engine !== engine) {
          return;
        }

        const primary = stats[0];
        if (!primary?.uid) {
          return;
        }

        this.onRemoteMediaStatsChange({
          hasVideoTrack: true,
          sampledAtMs: Date.now(),
          uid: primary.uid,
          videoBytesReceived: Number(primary.stats?.bytesReceived ?? 0),
          videoDecodeFps: Number(primary.stats?.framesPerSecond ?? 0),
          videoFramesDecoded: Number(primary.stats?.framesDecoded ?? 0),
          videoFramesReceived: Number(primary.stats?.framesReceived ?? 0),
          videoFramesRendered: Number(primary.stats?.framesRendered ?? 0),
          videoFrameHeight: Number(primary.stats?.frameHeight ?? 0),
          videoFrameWidth: Number(primary.stats?.frameWidth ?? 0),
          videoPacketsReceived: Number(primary.stats?.packetsReceived ?? 0),
        });
      });

      engine.on('rtcRemoteAudioStats', (stats: Array<{ stats?: Record<string, unknown>; uid?: string }>) => {
        if (this.disposed || this.engine !== engine) {
          return;
        }

        const primary = stats[0];
        if (!primary?.uid) {
          return;
        }

        this.onRemoteMediaStatsChange({
          audioBytesReceived: Number(primary.stats?.bytesReceived ?? 0),
          audioPacketsReceived: Number(primary.stats?.packetsReceived ?? 0),
          hasAudioTrack: true,
          sampledAtMs: Date.now(),
          uid: primary.uid,
        });
      });

      engine.on('localDeviceException', (deviceType, exceptionType, description) => {
        if (this.disposed || this.engine !== engine) {
          return;
        }

        const isDisplayDevice =
          deviceType === rtcSdk.AliRtcEngineLocalDeviceType.AliEngineLocalDeviceTypeDisplay ||
          exceptionType ===
            rtcSdk.AliRtcEngineLocalDeviceExceptionType.AliEngineLocalDeviceExceptionTypeDisplayException;
        if (!isDisplayDevice) {
          return;
        }

        warnScreenShare('localDeviceException', { deviceType, description, exceptionType });

        if (this.localVideoSource === 'screen') {
          this.handleScreenShareInterrupted().catch(() => {});
        }
      });

      engine.on('networkQuality', (event: { subscribe?: { loss?: number; quality?: number; rtt?: number } }) => {
        if (this.disposed || this.engine !== engine) {
          return;
        }

        this.onRemoteMediaStatsChange({
          sampledAtMs: Date.now(),
          subscribeLoss: event.subscribe?.loss,
          subscribeQuality: event.subscribe?.quality,
          subscribeRttMs: event.subscribe?.rtt,
          uid: null,
        });
      });

      if (rtcAuthInfo) {
        await engine.joinChannel(rtcAuthInfo, { userName: rtcUserId });
      } else {
        await engine.joinChannel(token, { userName: rtcUserId });
      }

      if (this.desiredLocalCameraEnabled) {
        await this.enableLocalCamera(engine, null);
      }
    } catch {
      if (this.engine === engine) {
        this.engine = null;
      }
      await teardownRtcEngine(engine);
      this.onConnectionFailure();
    }
  }

  async stop(): Promise<void> {
    const engine = this.engine;
    this.engine = null;
    this.remoteBindings = [];
    this.onRemoteBindingsChange([]);
    this.autoPlayBlockedAudioUsers.clear();
    this.detachScreenShareTrackListener();

    if (!engine) {
      this.setLocalVideoSource('none');
      return;
    }

    if (this.localVideoSource === 'screen') {
      await this.stopScreenShare(engine);
    }

    this.setLocalVideoSource('none');
    await teardownRtcEngine(engine);
  }

  async toggleLocalAudio(currentValue: boolean): Promise<void> {
    const nextValue = !currentValue;
    this.desiredLocalAudioEnabled = nextValue;

    const engine = this.engine;
    if (!engine) {
      return;
    }

    try {
      // 只切 mic 静音开关。mute 期间 SDK 自身的 BlankAudioPlugin
      // （engine 创建时 enableBlankAudio: true 启用）会持续往上行推空白音频，
      // 保持轨道活跃，避免对端 AI 判掉线。unmute 时也无需额外处理。
      engine.muteLocalMic(!nextValue);
      this.onLocalAudioEnabledChange(nextValue);
    } catch {
      // Keep previous UI state when SDK toggle fails.
    }
  }

  async toggleLocalCamera(currentValue: boolean, cameraPermissionErrorText: string): Promise<void> {
    const nextValue = !currentValue;
    this.desiredLocalCameraEnabled = nextValue;

    const engine = this.engine;
    if (!engine) {
      return;
    }

    if (this.localVideoSource === 'screen') {
      await this.stopScreenShare(engine);
    }

    if (!nextValue) {
      await this.disableLocalCamera(engine);
      return;
    }

    await this.enableLocalCamera(engine, cameraPermissionErrorText);
  }

  async toggleLocalScreenShare(
    currentValue: boolean,
    options: { permissionErrorText: string; unsupportedErrorText: string; startErrorText: string },
  ): Promise<void> {
    const engine = this.engine;
    if (!engine) {
      warnScreenShare('toggle_skipped', { reason: 'engine_missing' });
      return;
    }

    const nextValue = !currentValue;
    this.onScreenShareError(null);

    if (!nextValue) {
      await this.stopScreenShare(engine);
      return;
    }

    if (this.localVideoSource === 'screen') {
      await this.stopScreenShare(engine);
    }

    if (!isBrowserScreenShareSupported()) {
      warnScreenShare('start_skipped', { reason: 'unsupported' });
      this.onScreenShareSupportedChange(false);
      this.onScreenShareError(options.unsupportedErrorText);
      return;
    }

    let capturedStream: MediaStream | null = null;
    try {
      const displayMediaPromise = navigator.mediaDevices.getDisplayMedia({
        video: SCREEN_SHARE_VIDEO_CONSTRAINTS,
        audio: false,
      });

      if (this.localVideoSource === 'camera') {
        this.desiredLocalCameraEnabled = false;
        await this.stopLocalCameraBeforeScreenShare(engine);
      } else {
        this.desiredLocalCameraEnabled = false;
        this.onLocalCameraEnabledChange(false);
      }

      const stream = await displayMediaPromise;
      capturedStream = stream;
      const track = stream.getVideoTracks()[0];
      if (!track) {
        throw new Error('screen video track missing');
      }
      await clampScreenShareTrackConstraints(track);

      const rtcSdk = await this.getRtcSdk();
      const supported = await this.probeScreenShareSupport(rtcSdk);
      if (!supported) {
        warnScreenShare('start_skipped', { reason: 'unsupported' });
        this.onScreenShareError(options.unsupportedErrorText);
        stream.getTracks().forEach((streamTrack) => {
          streamTrack.stop();
        });
        capturedStream = null;
        return;
      }

      this.screenShareStream = stream;
      this.attachScreenShareTrackListener(track);
      // 把采集到的屏幕 track 塞进 camera 槽位（streamType=camera）上行，而非原生 screen
      // 辅流。原因：unidu 用的阿里 RTC Linux core service（黑盒二进制）只对 camera track
      // 回调远端视频裸帧（OnRemoteVideoSample），对 screen 辅流即使订阅成功也不抛裸帧，
      // 后端拿不到屏幕流。camera 槽位的裸帧链路已验证可用，故走 camera track。
      // 优先 publisher.createLocalStream（直接用原始 MediaStream），兜底 switchCamera 换 track。
      await engine.publishLocalVideoStream(false);
      await injectScreenTrackAsCamera(engine, stream, track);
      if (this.localVideoElement) {
        const rtcSdkForView = await this.getRtcSdk();
        await engine.setLocalViewConfig(this.localVideoElement, rtcSdkForView.AliRtcVideoTrack.AliRtcVideoTrackCamera);
      }
      await engine.publishLocalVideoStream(true);
      // createLocalStream 会把 display track 改回 camera 默认约 1280×720，发布后再压回 480p。
      await clampScreenShareTrackConstraints(track);
      this.setLocalVideoSource('screen');
    } catch (err) {
      if (isUserCancelledScreenShareError(err)) {
        warnScreenShare('start_cancelled', { error_name: err instanceof Error ? err.name : typeof err });
      } else {
        errorScreenShare('start_failed', err, { reason: 'sdk_error' });
      }

      await this.stopScreenShare(engine);
      if (capturedStream && capturedStream !== this.screenShareStream) {
        capturedStream.getTracks().forEach((track) => {
          track.stop();
        });
      }

      const shouldSurfaceError = !isUserCancelledScreenShareError(err);
      if (shouldSurfaceError) {
        this.onScreenShareError(options.startErrorText);
      }

      this.desiredLocalCameraEnabled = false;
    }
  }

  async restoreLocalMedia(): Promise<void> {
    const engine = this.engine;
    if (!engine) {
      return;
    }

    // Screen share track relies on user gesture (getDisplayMedia). It cannot be silently
    // re-acquired after reconnect, so reconnect drops it and only restores media that is still desired.
    if (this.localVideoSource === 'screen') {
      await this.stopScreenShare(engine);
    }

    await Promise.all([
      this.restoreLocalAudio(engine),
      this.desiredLocalCameraEnabled ? this.enableLocalCamera(engine, null) : this.disableLocalCamera(engine),
    ]);
  }

  resumePlayback(): Promise<void> | void {
    const engine = this.engine;
    if (engine) {
      engine.muteAllRemoteAudioPlaying(false);
      engine.setPlayoutVolume(100);
      this.autoPlayBlockedAudioUsers.forEach((uid) => {
        engine.resumeRemoteAudioStream(uid);
      });
      this.autoPlayBlockedAudioUsers.clear();
    }

    return this.remoteVideoElement?.play();
  }

  private applyRemoteView(): void {
    const engine = this.engine;
    const element = this.remoteVideoElement;
    const binding = pickDisplayBinding(this.remoteBindings);

    if (!engine || !element || !binding) {
      return;
    }

    engine.setRemoteViewConfig(element, binding.uid, binding.videoTrack);
  }

  private async getRtcSdk(): Promise<RtcSdkModule> {
    if (this.rtcSdk) {
      return this.rtcSdk;
    }

    this.rtcSdk = await this.loadRtcSdk();
    return this.rtcSdk;
  }

  private async restoreLocalAudio(engine: AliRtcEngine): Promise<void> {
    let lastError: unknown;

    for (const delayMs of LOCAL_AUDIO_RESTORE_RETRY_DELAYS_MS) {
      if (delayMs > 0) {
        await new Promise<void>((resolve) => {
          setTimeout(resolve, delayMs);
        });
      }

      if (this.disposed || this.engine !== engine) {
        return;
      }

      try {
        await engine.startAudioCapture({
          autoGainControl: true,
          echoCancellation: true,
          noiseSuppression: true,
        });
        await engine.publishLocalAudioStream(true);
        engine.muteLocalMic(!this.desiredLocalAudioEnabled);
        this.onLocalAudioEnabledChange(this.desiredLocalAudioEnabled);
        this.onLocalAudioRestoreError(null);
        return;
      } catch (error) {
        lastError = error;
      }
    }

    this.onLocalAudioRestoreError(normalizeError(lastError));
  }

  private async enableLocalCamera(engine: AliRtcEngine, cameraPermissionErrorText: string | null): Promise<void> {
    this.onCameraError(null);

    if (cameraPermissionErrorText) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        stream.getTracks().forEach((track) => {
          track.stop();
        });
      } catch {
        this.desiredLocalCameraEnabled = false;
        this.onCameraError(cameraPermissionErrorText);
        this.onLocalCameraEnabledChange(false);
        return;
      }
    }

    try {
      const rtcSdk = await this.getRtcSdk();
      await engine.enableLocalVideo(true);
      await engine.setCameraCapturerConfiguration({ frameRate: LOCAL_CAMERA_FRAME_RATE });
      if (this.localVideoElement) {
        await engine.setLocalViewConfig(this.localVideoElement, rtcSdk.AliRtcVideoTrack.AliRtcVideoTrackCamera);
      }
      await engine.startPreview();
      await engine.publishLocalVideoStream(true);
      this.onLocalCameraEnabledChange(true);
      this.setLocalVideoSource('camera');
    } catch (error) {
      this.desiredLocalCameraEnabled = false;
      this.onCameraError(normalizeError(error));
      this.onLocalCameraEnabledChange(false);
      if (this.localVideoSource === 'camera') {
        this.setLocalVideoSource('none');
      }
    }
  }

  private async disableLocalCamera(engine: AliRtcEngine): Promise<void> {
    try {
      await engine.publishLocalVideoStream(false);
      await engine.enableLocalVideo(false);
      await engine.stopPreview();
    } catch {
      // Do not block the rest of the call on local camera disable errors.
    }
    this.onLocalCameraEnabledChange(false);
    if (this.localVideoSource === 'camera') {
      this.setLocalVideoSource('none');
    }
  }

  private async stopLocalCameraBeforeScreenShare(engine: AliRtcEngine): Promise<void> {
    try {
      await engine.publishLocalVideoStream(false);
      await engine.stopPreview();
    } catch {
      // Do not block screen share start on local camera preview cleanup errors.
    }
    this.onLocalCameraEnabledChange(false);
    if (this.localVideoSource === 'camera') {
      this.setLocalVideoSource('none');
    }
  }

  private async stopScreenShare(engine: AliRtcEngine): Promise<void> {
    this.detachScreenShareTrackListener();
    // 屏幕采集走的是 camera 槽位（见 toggleLocalScreenShare），收尾与关摄像头一致：
    // 停 camera 上行 + 停预览。不再调 stopScreenShare/publishLocalScreenShareStream。
    try {
      await engine.publishLocalVideoStream(false);
      await engine.stopPreview();
    } catch (err) {
      warnScreenShare('stop_screen_share_error', {
        error_name: err instanceof Error ? err.name : typeof err,
        error_message: err instanceof Error ? err.message : String(err),
      });
    }
    this.screenShareStream?.getTracks().forEach((track) => {
      track.stop();
    });
    this.screenShareStream = null;

    if (this.localVideoSource === 'screen') {
      this.setLocalVideoSource('none');
    }
  }

  private async probeScreenShareSupport(rtcSdk: RtcSdkModule): Promise<boolean> {
    const browserSupport = isBrowserScreenShareSupported();
    let sdkSupport = false;
    try {
      const supportResult = await rtcSdk.default.isSupported();
      sdkSupport = Boolean(supportResult?.detail?.isScreenShareSupported);
    } catch (err) {
      warnScreenShare('sdk_isSupported_error', {
        error_name: err instanceof Error ? err.name : typeof err,
        error_message: err instanceof Error ? err.message : String(err),
      });
    }
    const supported = browserSupport && sdkSupport;
    this.onScreenShareSupportedChange(supported);
    return supported;
  }

  private attachScreenShareTrackListener(track: MediaStreamTrack): void {
    this.detachScreenShareTrackListener();

    const listener = (): void => {
      this.handleScreenShareInterrupted().catch(() => {});
    };

    track.addEventListener('ended', listener, { once: true });
    this.screenShareTrack = track;
    this.screenShareTrackEndedListener = listener;
  }

  private detachScreenShareTrackListener(): void {
    if (this.screenShareTrack && this.screenShareTrackEndedListener) {
      this.screenShareTrack.removeEventListener('ended', this.screenShareTrackEndedListener);
    }
    this.screenShareTrack = null;
    this.screenShareTrackEndedListener = null;
  }

  private async handleScreenShareInterrupted(): Promise<void> {
    if (this.localVideoSource !== 'screen') {
      return;
    }

    const engine = this.engine;
    if (!engine) {
      this.setLocalVideoSource('none');
      return;
    }

    await this.stopScreenShare(engine);
  }

  private setLocalVideoSource(source: LocalVideoSource): void {
    if (this.localVideoSource === source) {
      return;
    }
    this.localVideoSource = source;
    this.onLocalVideoSourceChange(source);
  }

  private removeRemoteBinding(uid: string): void {
    this.remoteBindings = this.remoteBindings.filter((item) => item.uid !== uid);
    this.onRemoteBindingsChange([...this.remoteBindings]);
  }

  private upsertRemoteBinding(binding: RemoteVideoBinding): void {
    this.remoteBindings = this.remoteBindings.filter(
      (item) => item.uid !== binding.uid || item.videoTrack !== binding.videoTrack,
    );
    this.remoteBindings.push(binding);
    this.onRemoteBindingsChange([...this.remoteBindings]);
    this.applyRemoteView();
  }

  dispose(): Promise<void> {
    this.disposed = true;
    return this.stop();
  }
}
